import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import type { UseFormGetValues } from 'react-hook-form';
import dayjs from 'dayjs';
import { TimekeeperService } from 'services/TimekeeperService';
import type { AccrualProfile } from 'repository/timekeeper/@types/AccrualProfile';
import type { AccrualProfileRow, FormValues } from 'repository/timekeeper/@types/AccrualProfile';

export const INDEFINITE_DATE = dayjs('3000-01-01');

const timekeeperService = new TimekeeperService();

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

type UseAccrualProfileFormProps = {
    employeeId: number;
    availableProfiles: AccrualProfile[];
    getValues: UseFormGetValues<FormValues>;
    onSuccess: () => void;
    onClose: () => void;
};

type UseAccrualProfileFormReturn = {
    validateRow: (row: AccrualProfileRow, index: number) => string | null;
    onSubmit: (formData: FormValues) => Promise<void>;
};

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

export const useAccrualProfileForm = ({
    employeeId,
    availableProfiles,
    getValues,
    onSuccess,
    onClose,
}: UseAccrualProfileFormProps): UseAccrualProfileFormReturn => {
    const { t } = useTranslation();

    // ── Validación ────────────────────────────────────────────

    const validateRow = useCallback(
        (row: AccrualProfileRow, index: number): string | null => {
            if (!row.accrual_profile_id)
                return t('timekeeper.employees.dialog.assign_accrual_profile.select_required');
            if (!row.start_date)
                return t('timekeeper.employees.dialog.assign_accrual_profile.start_date_required');
            if (!row.end_date)
                return t('timekeeper.employees.dialog.assign_accrual_profile.end_date_required');

            const s = row.start_date;
            const e = row.end_date;
            const all = getValues('profiles');

            if (s.isAfter(e) && !e.isSame(INDEFINITE_DATE, 'day'))
                return t('timekeeper.employees.dialog.assign_accrual_profile.date_range_invalid');

            const hasIndefinite = all.some(
                (r, idx) => idx !== index && r.end_date?.year() >= 3000,
            );
            if (hasIndefinite && e.isSame(INDEFINITE_DATE, 'day'))
                return t('timekeeper.employees.dialog.assign_accrual_profile.indefinite_exists');

            const overlappingIdx = all.findIndex((r, idx) => {
                if (idx === index || !r.start_date || !r.end_date) return false;
                return s.isBefore(r.end_date) && e.isAfter(r.start_date);
            });

            if (overlappingIdx !== -1) {
                const ov = all[overlappingIdx];
                const name = availableProfiles.find(
                    p => p.accrual_profile_id === ov.accrual_profile_id,
                )?.name;
                return `${t('timekeeper.employees.dialog.assign_accrual_profile.date_overlap')}: "${name}" (${ov.start_date?.format('DD/MM/YYYY')} - ${ov.end_date?.format('DD/MM/YYYY')})`;
            }

            return null;
        },
        [t, availableProfiles, getValues],
    );

    // ── Submit ────────────────────────────────────────────────

    const onSubmit = useCallback(
        async (formData: FormValues) => {
            // Validación previa al request
            const hasErrors = formData.profiles.some((row, i) => validateRow(row, i) !== null);
            if (hasErrors) {
                enqueueSnackbar(
                    t('timekeeper.employees.dialog.assign_accrual_profile.alerts.validation_error'),
                    { variant: 'error' },
                );
                return;
            }

            const payload = formData.profiles.map(row => ({
                accrual_profile_r_employee_id: row.employee_accrual_profile_id,
                accrual_profile_id: row.accrual_profile_id as number,
                employee_id: row.employee_id,
                start_date: row.start_date.format('YYYY-MM-DD'),
                end_date: row.end_date.format('YYYY-MM-DD'),
            }));

            const toCreate = payload.filter(p => p.accrual_profile_r_employee_id === null);
            const toUpdate = payload.filter(p => p.accrual_profile_r_employee_id !== null);

            try {
                await Promise.all([
                    toCreate.length > 0
                        ? timekeeperService.employee_accrual_profile.create(
                            toCreate.map(({ accrual_profile_r_employee_id: _, ...rest }) => rest),
                        )
                        : Promise.resolve(),
                    toUpdate.length > 0
                        ? timekeeperService.employee_accrual_profile.update(employeeId, toUpdate)
                        : Promise.resolve(),
                ]);

                enqueueSnackbar(
                    t('timekeeper.employees.dialog.assign_accrual_profile.alerts.success'),
                    { variant: 'success' },
                );
                onSuccess();
                onClose();
            } catch (e) {
                console.error('Error saving accrual profiles:', e);
                enqueueSnackbar(
                    t('timekeeper.employees.dialog.assign_accrual_profile.alerts.error'),
                    { variant: 'error' },
                );
            }
        },
        [t, employeeId, validateRow, onSuccess, onClose],
    );

    return { validateRow, onSubmit };
};